<template>
  <div class="setting-panel">
    <header class="setting-panel__head">
      <slot name="leading" />
      <span v-if="icon" class="setting-panel__icon">
        <v-icon color="primary">{{ icon }}</v-icon>
      </span>
      <div class="setting-panel__heading">
        <div class="setting-panel__title">{{ title }}</div>
        <div v-if="subtitle" class="setting-panel__subtitle">{{ subtitle }}</div>
      </div>
      <div v-if="$slots.actions" class="setting-panel__actions">
        <slot name="actions" />
      </div>
    </header>
    <div v-if="$slots.tabs" class="setting-panel__tabs">
      <slot name="tabs" />
    </div>
    <div ref="body" class="setting-panel__body" :class="{ 'setting-panel__body--flush': flush, 'setting-panel__body--fill': fill }">
      <slot />
    </div>
    <footer v-if="$slots.footer" class="setting-panel__foot">
      <slot name="footer" />
    </footer>
  </div>
</template>

<script>
// Section frame: header and footer stay put, only the body scrolls.
export default {
  name: 'SettingPanel',
  props: {
    icon: { type: String, default: null },
    title: { type: String, default: '' },
    subtitle: { type: String, default: null },
    flush: { type: Boolean, default: false },
    // Child manages its own scroll/footer and fills the body
    fill: { type: Boolean, default: false }
  },
  methods: {
    scrollTop () {
      if (this.$refs.body) this.$refs.body.scrollTop = 0
    }
  }
}
</script>

<style scoped>
.setting-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}
.theme--dark .setting-panel {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.12);
}
.setting-panel__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.setting-panel__icon {
  flex: 0 0 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.1);
}
.setting-panel__heading {
  flex: 1 1 auto;
  min-width: 0;
}
.setting-panel__title {
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-panel__subtitle {
  margin-top: 2px;
  font-size: 0.78rem;
  line-height: 1.5;
  opacity: 0.6;
}
.setting-panel__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
}
.setting-panel__tabs {
  flex: 0 0 auto;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.setting-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Legacy content (v-row negative margins) can be a few px too wide; a
     horizontal scrollbar would leave a strip under the sticky footer. */
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 20px 18px;
}
.setting-panel__body--fill {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.setting-panel__body--fill ::v-deep > * {
  flex: 1 1 auto;
  min-height: 0;
}
/* Legacy modules (invoice print, payment gateways) are
   v-card > v-form > ... > v-card-actions. The form itself is the scroll
   area and the actions are its last child, pinned with sticky + pushed down
   with margin-top:auto — so short pages get the footer at the bottom and long
   pages keep it fixed, with nothing able to sit below it. */
.setting-panel__body--flush {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.setting-panel__body--flush ::v-deep > * {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.setting-panel__body--flush ::v-deep > * > .v-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 8px 8px 0;
}
.setting-panel__body--flush ::v-deep > * > .v-form > * {
  flex-shrink: 0;
}
.setting-panel__body--flush ::v-deep .v-form > .v-card__actions {
  flex: 0 0 auto;
  position: sticky;
  bottom: 0;
  z-index: 3;
  margin: auto -8px 0 !important;
  padding: 12px 18px !important;
  background: #fafbfd;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
}
.theme--dark .setting-panel__body--flush ::v-deep .v-form > .v-card__actions {
  background: #262626;
}
.setting-panel__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  background: #fafbfd;
}
.theme--dark .setting-panel__foot {
  background: rgba(255, 255, 255, 0.03);
}
</style>
